import { LitElement, html, nothing, css, unsafeCSS } from 'lit';
import { FormValidationController } from '@/controllers/FormValidationController.js';
import { createRef, ref } from 'lit/directives/ref.js';
import styles from './style.scss?inline';
import { checkValidity } from '@/components/input/common.js';

export default class SotbitCheckbox extends LitElement {
    static formAssociated = true;

    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        name: { type: String },
        value: { type: String },
        required: { type: Boolean, reflect: true },
        indeterminate: { type: Boolean, reflect: true },
        checked: { type: Boolean, reflect: true },
        disabled: { type: Boolean, reflect: true },
        _defaultValue: { type: Boolean, state: true },
        _defaultChecked: { type: Boolean, state: true },
    };

    constructor() {
        super();

        this.value = 'on';

        this._internals = this.attachInternals();
        this._validationController = new FormValidationController(this, {
            getValue: () => this.getValue(),
            getInputRef: () => this.inputRef,
        });

        this.handleUpdateFormValue = this.updateFormValue.bind(this);
        this.inputRef = createRef();
    }

    set checked(value) {
        const oldValue = this._checked;

        if (this.inputRef?.value) {
            this.inputRef.value.checked = value;
        }

        this._checked = value;
        this._internals?.setFormValue(this.checked ? this.value : null, this.checked);

        this.requestUpdate('checked', oldValue);
    }

    get checked() {
        return this._checked;
    }

    get type() {
        return 'checkbox';
    }

    firstUpdated(_changedProperties) {
        super.firstUpdated(_changedProperties);

        this.value = this.value || 'on';
        this._defaultValue = this.value;
        this.checked = this.checked || false;
        this._defaultChecked = this.checked;
        this.name = this.name || '';
        this.required = this.required || false;
        this.disabled = this.disabled || false;
    }

    connectedCallback() {
        super.connectedCallback();

        if (!this.getAttribute('tabindex')) {
            this.setAttribute('tabindex', '0');
        }

        this.addEventListener('change', this.handleUpdateFormValue);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('change', this.handleUpdateFormValue);
    }

    getValue() {
        return this.checked ? this.value || 'on' : '';
    }

    updateFormValue(isValidate = true) {
        this._internals.setFormValue(this.checked ? this.value : null, this.checked);

        if (isValidate) {
            this._validationController.validate();
        }
    }

    formAssociatedCallback() {
        this.updateFormValue(false);
    }

    formDisabledCallback(disabled) {
        this.disabled = disabled;
    }

    formResetCallback() {
        this.value = this._defaultValue;
        this.checked = this._defaultChecked;

        if (this.inputRef?.value) {
            this.inputRef.value.checked = this.checked;
            this.inputRef.value.value = this.value;
            this.inputRef.value.toggleAttribute('checked', this.checked);
        }

        this.updateFormValue(false);
    }

    formStateRestoreCallback(state) {
        this.checked = state === 'true';

        if (this.inputRef?.value) {
            this.inputRef.value.checked = this.checked;
        }

        this.dispatchEvent(
            new CustomEvent('formStateRestoreCallback', {
                bubbles: true,
                composed: true,
                detail: {
                    value: state,
                },
            })
        );
    }

    checkValidity(reportValidity) {
        return checkValidity(this, reportValidity);
    }

    reportValidity() {
        return this.checkValidity(true);
    }

    focus(options) {
        this.inputRef?.value?.focus(options);
    }

    onChange(event) {
        this.checked = event.target.checked;
        this.updateFormValue();
    }

    render() {
        return html`
            <sotbit-checkbox-style class="checkbox-style" exportparts="checkbox-wrapper, icon-box, icon, label-wrapper">
                <slot slot="icon-left" name="icon-left"></slot>
                <input
                    type="checkbox"
                    name="${this.name || nothing}"
                    value="${this.value || nothing}"
                    ?checked="${this.checked}"
                    ?disabled="${this.disabled}"
                    ?indeterminate="${this.indeterminate}"
                    ?required="${this.required}"
                    ${ref(this.inputRef)}
                    slot="checkbox"
                    @change="${this.onChange}"
                />
                <slot name="label" slot="label"></slot>
                <slot slot="icon-right" name="icon-right"></slot>
            </sotbit-checkbox-style>
        `;
    }
}
